import { dateToX } from './wbsUtils';

interface Props {
  minDate: Date;
  maxDate: Date;
  dayWidth: number;
  totalWidth: number;
  timeUnit: 'day' | 'week' | 'month';
}

const DAY_LABELS = ['일', '월', '화', '수', '목', '금', '토'];

export default function WbsGanttHeader({ minDate, maxDate, dayWidth, totalWidth, timeUnit }: Props) {
  // day 모드: 2행 헤더 (날짜 + 요일)
  if (timeUnit === 'day') {
    return renderDayHeader(minDate, maxDate, dayWidth, totalWidth);
  }

  // week/month 모드: 기존 1행 헤더
  return renderDefaultHeader(minDate, maxDate, dayWidth, totalWidth);
}

function renderDayHeader(minDate: Date, maxDate: Date, dayWidth: number, totalWidth: number) {
  const days: { date: Date; x: number }[] = [];
  const current = new Date(minDate);

  while (current <= maxDate) {
    days.push({
      date: new Date(current),
      x: dateToX(current, minDate, dayWidth),
    });
    current.setDate(current.getDate() + 1);
  }

  return (
    <div className="relative border-b bg-gray-50" style={{ width: totalWidth, height: 42 }}>
      {days.map((d) => {
        const dow = d.date.getDay();
        const isWeekend = dow === 0 || dow === 6;
        const dayColor = dow === 0 ? 'text-red-400' : dow === 6 ? 'text-blue-400' : 'text-gray-400';

        return (
          <div
            key={d.date.toISOString()}
            className={`absolute top-0 bottom-0 border-r border-gray-200 flex flex-col items-center justify-center ${
              isWeekend ? 'bg-gray-100/50' : ''
            }`}
            style={{ left: d.x, width: dayWidth }}
          >
            <span className="text-sm text-gray-600 leading-tight">
              {d.date.getMonth() + 1}/{d.date.getDate()}
            </span>
            <span className={`text-[11px] leading-tight ${dayColor}`}>
              {DAY_LABELS[dow]}
            </span>
          </div>
        );
      })}
    </div>
  );
}

function renderDefaultHeader(minDate: Date, maxDate: Date, dayWidth: number, totalWidth: number) {
  const ticks: { label: string; x: number; isMajor: boolean }[] = [];
  const current = new Date(minDate);

  while (current <= maxDate) {
    const day = current.getDate();
    const isMonday = current.getDay() === 1;
    const isFirst = day === 1;

    if (isFirst) {
      ticks.push({
        label: `${current.getFullYear()}/${current.getMonth() + 1}`,
        x: dateToX(current, minDate, dayWidth),
        isMajor: true,
      });
    } else if (isMonday && dayWidth >= 3) {
      ticks.push({
        label: `${current.getMonth() + 1}/${day}`,
        x: dateToX(current, minDate, dayWidth),
        isMajor: false,
      });
    }
    current.setDate(current.getDate() + 1);
  }

  return (
    <div className="relative border-b bg-gray-50 h-[42px]" style={{ width: totalWidth }}>
      {ticks.map((t, i) => (
        <div
          key={i}
          className="absolute top-0 h-full border-l border-gray-200 flex items-end"
          style={{ left: t.x }}
        >
          <span
            className={`px-1 pb-1 whitespace-nowrap ${
              t.isMajor ? 'text-sm font-medium text-gray-600' : 'text-[11px] text-gray-400'
            }`}
          >
            {t.label}
          </span>
        </div>
      ))}
    </div>
  );
}
